import type { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import {
  BookingType,
  type NotSerializedBookingInputs
} from "../../../features/Booking-Form/typings/booking-inputs";
import { isTeamWorkplaceSeatAssignee } from "./form.functions";
import {
  bookingObjectExternal,
  bookingUserParsed,
  externalParsed,
  firstNameParsed,
  isBookingUser,
  surnameParsed,
  teamMemberParsed,
  timezoneParsed,
  visitorInformationFromUser
} from "./form-confirm.functions";

/**
 * Team workplace + invited visitor(s): confirm uses POST /booking/visitor with a full-party payload.
 * Pure team (no visitor rows) stays on POST /booking/place via {@link teamWorkplaceUsesVisitorGroupApi} routing.
 */
export function teamWorkplaceUsesVisitorGroupApi(inputs: NotSerializedBookingInputs): boolean {
  if (inputs.mode !== BookingType.TEAM) {
    return false;
  }
  if (inputs.bookingType !== BookingType.WORKPLACE) {
    return false;
  }
  return (inputs.usersBookedFor ?? []).some(
    u => u.isVisitor === true || u.visitorInformation != null
  );
}

function booksWorkplaceDesk(user: TeamMember, inputs: NotSerializedBookingInputs): boolean {
  if (inputs.isMobileWorking) {
    return true;
  }
  const id = user.bookingInventoryId;
  return id != null && id > 0;
}

/**
 * When any guest is visit-only, prefer the visit-only location from registrations (same idea as visitor booking strategy).
 */
export function resolveTeamVisitorGroupLocationInventoryId(
  inputs: NotSerializedBookingInputs
): number | null {
  const hasVisitOnlyGuest = (inputs.usersBookedFor ?? []).some(
    u => (u.isVisitor === true || u.visitorInformation != null) && u.bookPlace !== true
  );
  if (!hasVisitOnlyGuest) {
    return inputs.selectedLocation ?? null;
  }
  const visitOnlyRegistration = inputs.visitorRegistrations?.find(
    registration => registration?.bookPlace !== true
  );
  const visitOnlyLocation = visitOnlyRegistration?.visitOnlySelectedLocation;
  if (visitOnlyLocation != null && !Number.isNaN(Number(visitOnlyLocation))) {
    return Number(visitOnlyLocation);
  }
  return inputs.selectedLocation ?? null;
}

/**
 * Full-party `users` array for POST /booking/visitor (mixed team + visitors).
 *
 * Backend contract (weplace `createUserBookings` with `skipAvailabilityChecks`):
 * - Desk rows must send `bookPlace: true` explicitly; omitting it is treated like visit-only.
 * - Visit-only rows: `bookPlace: false`, no desk `bookingInventoryId`, `isMobileWorking: true` so resource checks do not see bad inventory ids.
 * - Every `userId` must resolve via `getBookingUsersByIds` (guest rows reuse the booker id where the product already does that).
 */
export function buildTeamVisitorGroupBookingUsers(
  usersBookedFor: TeamMember[] | undefined,
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string
): TeamMember[] {
  const list = usersBookedFor ?? [];
  return list.map(user => {
    if (isBookingUser(user, sub)) {
      if (booksWorkplaceDesk(user, inputs)) {
        return {
          ...bookingUserParsed(user, sub, inputs, timezoneMobileWorking, true),
          bookPlace: true
        };
      }
      return {
        ...bookingUserParsed(user, sub, inputs, timezoneMobileWorking, true),
        bookPlace: false,
        isMobileWorking: true,
        bookingInventoryId: undefined
      };
    }

    if (user.isVisitor === true || user.visitorInformation != null) {
      const visitorBooksDesk = user.bookPlace === true;
      if (visitorBooksDesk) {
        return {
          userId: sub,
          isUserBookedBy: false,
          userBookedById: sub,
          visitorInformation: visitorInformationFromUser(user),
          isVisitor: true,
          bookPlace: true,
          timezone: timezoneParsed(inputs, timezoneMobileWorking),
          bookingInventoryId: bookingObjectExternal(user, inputs),
          firstName: firstNameParsed(user, true),
          surname: surnameParsed(user, true),
          isMobileWorking: inputs.isMobileWorking
        };
      }
      return {
        userId: sub,
        isUserBookedBy: false,
        userBookedById: sub,
        visitorInformation: visitorInformationFromUser(user),
        isVisitor: true,
        bookPlace: false,
        timezone: timezoneParsed(inputs, timezoneMobileWorking),
        bookingInventoryId: undefined,
        firstName: firstNameParsed(user, true),
        surname: surnameParsed(user, true),
        isMobileWorking: true
      };
    }

    if (user.isExternal === true) {
      const deskRow = booksWorkplaceDesk(user, inputs) && isTeamWorkplaceSeatAssignee(user);
      if (deskRow) {
        return {
          ...externalParsed(user, sub, inputs, timezoneMobileWorking, true),
          bookPlace: true,
          isMobileWorking: inputs.isMobileWorking
        };
      }
      return {
        ...externalParsed(user, sub, inputs, timezoneMobileWorking, true),
        bookPlace: false,
        isMobileWorking: true,
        bookingInventoryId: undefined
      };
    }

    if (booksWorkplaceDesk(user, inputs)) {
      return {
        ...teamMemberParsed(user, inputs, sub, true),
        bookPlace: true
      };
    }
    return {
      ...teamMemberParsed(user, inputs, sub, true),
      bookPlace: false,
      isMobileWorking: true,
      bookingInventoryId: undefined
    };
  });
}
